import { useEffect, useState, type ReactNode } from "react";
import type { Construction, DefaultMaterialRow } from "../api/types.js";
import { ApiError } from "../api/client.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";

/**
 * Default materials (migration 0056): one construction per kind of surface,
 * chosen once for the whole survey. Every surface of that kind nobody settled
 * takes it — and follows it when it changes — while a surface somebody set on
 * its own is kept and counted. The heat loss follows.
 */
export function DefaultMaterialsCard({
  surveyId,
  canEdit,
  onChanged,
  revision = 0,
}: {
  readonly surveyId: string;
  readonly canEdit: boolean;
  readonly onChanged: () => void;
  /** Bumped when the design conditions change: internal walls and doors may now count, or not. */
  readonly revision?: number;
}): ReactNode {
  const { client } = usePortal();
  const { state } = useAsync(async () => client.defaultMaterials(surveyId), [client, surveyId, revision]);
  const [rows, setRows] = useState<readonly DefaultMaterialRow[] | null>(null);
  useEffect(() => setRows(null), [revision]);
  const [said, setSaid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [lists, setLists] = useState<Record<string, readonly Construction[]>>({});
  const shown = rows ?? (state.status === "ready" ? state.data : null);
  if (!shown) return null;
  const present = shown.filter((r) => r.surfaces > 0);
  if (present.length === 0) return null;

  const load = (element: DefaultMaterialRow["element"]): void => {
    if (lists[element]) return;
    client
      .constructions(element)
      .then((list) => setLists((all) => ({ ...all, [element]: list })))
      .catch(() => setError("The construction list could not be loaded."));
  };

  const apply = (row: DefaultMaterialRow, constructionId: string): void => {
    if (!constructionId) return;
    setBusy(row.target);
    setError(null);
    setSaid(null);
    client
      .setDefaultMaterial(surveyId, row.target, constructionId)
      .then((res) => {
        setRows(res.rows);
        setSaid(
          `${row.label}: applied to ${res.applied} surface${res.applied === 1 ? "" : "s"}` +
            (res.kept > 0 ? `, ${res.kept} set by hand kept.` : "."),
        );
        onChanged();
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "The change could not be saved."))
      .finally(() => setBusy(null));
  };

  return (
    <section className="default-materials" data-testid="default-materials">
      <h2>Default materials</h2>
      <p className="muted small">
        One choice sets every surface of that kind. Anything set by hand on a single wall or window is kept.
      </p>
      <div className="default-materials__rows">
        {present.map((row) => (
          <div key={row.target} className="default-materials__row">
            <div className="default-materials__what">
              <span className="default-materials__name">{row.label}</span>
              <span className="muted small">
                {row.surfaces} {nounFor(row.target, row.surfaces, row.label)} · {row.areaM2} m²
                {row.byHand > 0 ? ` · ${row.byHand} set by hand` : ""}
              </span>
            </div>
            <div className="default-materials__now small">
              {row.notCounted ? (
                <span data-testid="default-materials-not-counted">
                  <b>0.00</b> W/m²K · not counted between heated rooms (Design conditions)
                </span>
              ) : row.defaultConstruction ? (
                <>
                  <b>{row.defaultConstruction.uValue.toFixed(2)}</b> W/m²K · {row.defaultConstruction.label}
                </>
              ) : row.commonUValue !== null ? (
                <span className="muted">No default yet · mostly {row.commonUValue.toFixed(2)} W/m²K now</span>
              ) : (
                <span className="muted">No default yet</span>
              )}
            </div>
            {canEdit && !row.notCounted ? (
              <select
                aria-label={`Default for ${row.label.toLowerCase()}`}
                className="default-materials__pick"
                value=""
                disabled={busy !== null}
                onFocus={() => load(row.element)}
                onMouseDown={() => load(row.element)}
                onChange={(e) => apply(row, e.target.value)}
              >
                <option value="">{busy === row.target ? "Applying…" : "Change…"}</option>
                {(lists[row.element] ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        ))}
      </div>
      {said ? (
        <p className="small" role="status" data-testid="default-materials-said">
          {said}
        </p>
      ) : null}
      {error ? (
        <p className="notice error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

const NOUNS: Readonly<Record<string, readonly [string, string]>> = {
  "external-wall": ["external wall", "external walls"],
  "party-wall": ["party wall", "party walls"],
  "internal-wall": ["internal wall", "internal walls"],
  "unheated-wall": ["wall to an unheated space", "walls to unheated spaces"],
  roof: ["roof", "roofs"],
  window: ["window", "windows"],
  door: ["external door", "external doors"],
  "internal-door": ["internal door", "internal doors"],
  "unheated-door": ["door to an unheated space", "doors to unheated spaces"],
};

/** "4 external walls", "1 window". */
function nounFor(target: string, n: number, label: string): string {
  const pair = NOUNS[target];
  return pair ? pair[n === 1 ? 0 : 1] : label.toLowerCase();
}
